import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Sparkles, RefreshCw } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { centsToInputValue, dollarsInputToCents } from "@/lib/money";
import { ServiceImageManager } from "@/components/org/ServiceImageManager";

const PRICE_TYPES = [
  { value: "fixed", label: "Fixed price" },
  { value: "starting_at", label: "Starting price" },
  { value: "custom_quote", label: "Custom quote" },
];

const inputClass = "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 disabled:opacity-60 dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

export const ServiceEditModal = ({ service, canEdit, canManageImages, onClose, onUpdated, onRefreshNow }) => {
  const { API } = useAuth();
  const [form, setForm] = useState({
    name: service.name || "",
    service_type: service.service_type || "",
    duration_minutes: service.duration_minutes ?? "",
    description: service.description || "",
    price_type: service.price_type || "fixed",
    price: service.price_cents != null ? centsToInputValue(service.price_cents) : "",
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const images = service.images || [];
  const primary = images.find((img) => img.is_primary) || images[0];
  const status = service.image_generation_status;

  useEffect(() => {
    if (status === "pending") {
      pollRef.current = setInterval(() => onRefreshNow(), 3000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const save = async () => {
    if (!form.name.trim()) { setError("Service name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/services/${service.service_id}`, {
        name: form.name.trim(),
        service_type: form.service_type.trim() || null,
        duration_minutes: form.duration_minutes !== "" ? parseInt(form.duration_minutes, 10) : null,
        description: form.description.trim() || null,
        price_type: form.price_type,
        price_cents: form.price_type === "custom_quote" ? null : dollarsInputToCents(form.price),
      }, { withCredentials: true });
      onUpdated(data.service);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const triggerGenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/org/services/${service.service_id}/images/generate`, {}, { withCredentials: true });
      onUpdated(data.service);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start image generation.");
    } finally {
      setGenerating(false);
    }
  };

  const canRegenerate = images.length === 1 && primary?.source === "ai_generated" && status !== "pending";
  const canRetry = images.length === 0 && status === "failed";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="service-edit-overlay">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="service-edit-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Edit Service</h2>
          <button type="button" onClick={onClose} data-testid="service-edit-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="mt-4">
          <label className={labelClass}>Service Image</label>
          <div className="mt-2">
            {status === "pending" && (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" data-testid="service-edit-generating-banner">
                <RefreshCw size={12} className="animate-spin" /> Generating an AI image for this service…
              </div>
            )}
            {status === "failed" && images.length === 0 && (
              <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-500/10" data-testid="service-edit-generation-error">
                <p className="text-xs font-bold text-red-700 dark:text-red-400">Image generation failed</p>
                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{service.image_generation_error || "We couldn't generate an image for this service."}</p>
              </div>
            )}
            {canManageImages ? (
              <ServiceImageManager service={service} onUpdated={onUpdated} />
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">You don't have permission to manage this service's image.</p>
            )}
            {canManageImages && (canRegenerate || canRetry) && (
              <button
                type="button"
                onClick={triggerGenerate}
                disabled={generating}
                data-testid="service-edit-regenerate-btn"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
              >
                <Sparkles size={12} /> {generating ? "Starting…" : canRetry ? "Generate Image" : "Regenerate Image"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Service Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEdit} data-testid="service-edit-name-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Service Type</label>
            <input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} disabled={!canEdit} data-testid="service-edit-type-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Duration (minutes)</label>
            <input type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} disabled={!canEdit} data-testid="service-edit-duration-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Pricing</label>
            <select value={form.price_type} onChange={(e) => setForm({ ...form, price_type: e.target.value })} disabled={!canEdit} data-testid="service-edit-price-type" className={inputClass}>
              {PRICE_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {form.price_type !== "custom_quote" && (
            <div>
              <label className={labelClass}>Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} disabled={!canEdit} data-testid="service-edit-price-input" className={inputClass} />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} disabled={!canEdit} data-testid="service-edit-description-input" className={inputClass} />
          </div>
        </div>

        {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="service-edit-error">{error}</p>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="service-edit-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
            {canEdit ? "Cancel" : "Close"}
          </button>
          {canEdit && (
            <button type="button" onClick={save} disabled={saving} data-testid="service-edit-save-btn" className="rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
